'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useT } from '@/components/i18n-provider';
import { detectPlatformEmbed } from '@/lib/platform-registry';
import { PlatformEmbed, RichLinkCard, type LinkMeta } from '@/components/platform-embed';
import { ExternalLink, Link2, Copy, Check } from 'lucide-react';

const ogCache = new Map<string, LinkMeta | null>();
const inflight = new Map<string, Promise<LinkMeta | null>>();

async function fetchOG(url: string): Promise<LinkMeta | null> {
  if (ogCache.has(url)) return ogCache.get(url)!;
  if (inflight.has(url)) return inflight.get(url)!;

  const promise = (async () => {
    try {
      const res = await fetch(`/api/og?url=${encodeURIComponent(url)}`);
      if (!res.ok) { ogCache.set(url, null); return null; }
      const data = await res.json();
      const meta: LinkMeta = {
        title: data.title,
        description: data.description,
        image: data.imageLocal || data.image,
        siteName: data.siteName,
      };
      if (!meta.title && !meta.image) { ogCache.set(url, null); return null; }
      ogCache.set(url, meta);
      return meta;
    } catch {
      ogCache.set(url, null);
      return null;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, promise);
  return promise;
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    const display = u.hostname + path;
    return display.length > 50 ? display.slice(0, 47) + '...' : display;
  } catch {
    return url.length > 50 ? url.slice(0, 47) + '...' : url;
  }
}

export function LinkBar({ url, internal }: { url: string; internal?: boolean }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const Icon = internal ? Link2 : ExternalLink;

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  return (
    <span className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1 min-w-0 hover:text-foreground transition-colors"
      >
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">{truncateUrl(url)}</span>
      </a>
      <button
        onClick={handleCopy}
        className="shrink-0 p-0.5 rounded hover:bg-accent transition-colors"
        aria-label={t('posts.copyLink')}
      >
        {copied
          ? <Check className="h-3 w-3 text-emerald-500" />
          : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

export function LinkPreview({
  url,
  children,
}: {
  url: string;
  children: React.ReactNode;
}) {
  const platformMatch = detectPlatformEmbed(url);
  if (platformMatch) {
    return (
      <span className="block my-2 not-prose w-full max-w-lg">
        <PlatformEmbed match={platformMatch} />
        <LinkBar url={url} />
      </span>
    );
  }

  return <OGLinkPreview url={url}>{children}</OGLinkPreview>;
}

function OGLinkPreview({ url, children }: { url: string; children: React.ReactNode }) {
  const [meta, setMeta] = useState<LinkMeta | null>(() => ogCache.get(url) ?? null);
  const [fetched, setFetched] = useState(() => ogCache.has(url));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (fetched) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        io.disconnect();
        fetchOG(url).then((m) => { setMeta(m); setFetched(true); });
      },
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [url, fetched]);

  if (meta) {
    return (
      <span ref={ref} className="block not-prose max-w-md">
        <RichLinkCard url={url} meta={meta} />
        <LinkBar url={url} />
      </span>
    );
  }

  return (
    <span ref={ref} className="inline">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-blue-500 hover:underline break-all"
      >
        {children}
      </a>
    </span>
  );
}
